/**
 * Discriminator for every classified network-related error.
 * Downstream code (logging policy, display copy, retry behavior) switches
 * on this value instead of re-parsing errors.
 */
export type NetworkErrorType =
  | 'network'
  | 'server'
  | 'client'
  | 'auth'
  | 'timeout'
  | 'graphql';

/**
 * Base class for all network-related errors thrown by the centralized
 * error handlers (see errorHandlers.ts).
 */
export abstract class BaseNetworkError extends Error {
  public abstract readonly type: NetworkErrorType;
  public readonly timestamp: Date;
  public readonly originalError?: Error;

  constructor(message: string, originalError?: Error) {
    super(message);
    this.name = this.constructor.name;
    this.timestamp = new Date();
    this.originalError = originalError;

    // Maintains proper stack trace for where our error was thrown (V8 only)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, this.constructor);
    }
  }
}

/**
 * Network communication failures: DNS resolution, connection refused,
 * no internet, etc. Transient — never logged to Sentry.
 */
export class NetworkError extends BaseNetworkError {
  public readonly type = 'network';
}

/**
 * Server-side errors (5xx status codes). Actionable — logged to Sentry.
 * `serverMessage` carries a user-presentable message extracted from the
 * response body, when the server provided one.
 */
export class ServerError extends BaseNetworkError {
  public readonly type = 'server';
  public readonly statusCode: number;
  public readonly serverMessage?: string;
  public readonly responseBody?: unknown;

  constructor(
    message: string,
    statusCode: number,
    serverMessage?: string,
    responseBody?: unknown,
  ) {
    super(message);
    this.statusCode = statusCode;
    this.serverMessage = serverMessage;
    this.responseBody = responseBody;
  }
}

/**
 * Client-side errors (4xx status codes) — the server responded but the
 * request was invalid. Expected failures — not logged to Sentry.
 */
export class ClientError extends BaseNetworkError {
  public readonly type: NetworkErrorType = 'client';
  public readonly statusCode: number;
  public readonly responseBody?: unknown;

  constructor(message: string, statusCode: number, responseBody?: unknown) {
    super(message, undefined);
    this.statusCode = statusCode;
    this.responseBody = responseBody;
  }
}

/**
 * Authentication errors (401, 403). Expected on token expiration — not
 * logged to Sentry; the auth layer handles logout/refresh.
 */
export class AuthenticationError extends ClientError {
  public override readonly type = 'auth';
}

/**
 * Client-side request configuration issues: timeout, abort, bad request
 * setup. Transient or intentional — not logged to Sentry.
 */
export class RequestConfigError extends BaseNetworkError {
  public readonly type = 'timeout';
}

/**
 * GraphQL-layer errors (resolver/schema failures surfaced in the `errors`
 * array of an otherwise successful HTTP response). Actionable — logged.
 *
 * Named GraphQLOperationError to avoid clashing with the `GraphQLError`
 * class exported by the `graphql` package.
 */
export class GraphQLOperationError extends BaseNetworkError {
  public readonly type = 'graphql';
  public readonly graphqlErrors: ReadonlyArray<{ message: string }>;

  constructor(
    message: string,
    graphqlErrors: ReadonlyArray<{ message: string }> = [],
    originalError?: Error,
  ) {
    super(message, originalError);
    this.graphqlErrors = graphqlErrors;
  }
}

export const isNetworkError = (error: unknown): error is NetworkError =>
  error instanceof NetworkError;

export const isServerError = (error: unknown): error is ServerError =>
  error instanceof ServerError;

export const isClientError = (error: unknown): error is ClientError =>
  error instanceof ClientError;

export const isAuthenticationError = (
  error: unknown,
): error is AuthenticationError => error instanceof AuthenticationError;

export const isRequestConfigError = (
  error: unknown,
): error is RequestConfigError => error instanceof RequestConfigError;

export const isGraphQLOperationError = (
  error: unknown,
): error is GraphQLOperationError => error instanceof GraphQLOperationError;

export const isBaseNetworkError = (error: unknown): error is BaseNetworkError =>
  error instanceof BaseNetworkError;
